import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ShoppingCart, Check, Plus, Minus } from 'lucide-react';

export default function GroceryListModal({ open, onOpenChange, selectedRecipes, allRecipes, foodBankItems }) {
  const [checkedItems, setCheckedItems] = useState(new Set());
  const [groceryList, setGroceryList] = useState({});
  const [portions, setPortions] = useState(1);

  useEffect(() => {
    if (open && selectedRecipes.size > 0) {
      generateGroceryList();
    }
  }, [open, selectedRecipes, portions]);

  const generateGroceryList = () => {
    const ingredientMap = new Map();

    // Get selected recipe objects
    const recipes = allRecipes.filter(r => selectedRecipes.has(r.id));

    // Combine ingredients with portion multiplier
    recipes.forEach(recipe => {
      recipe.ingredients?.forEach(ing => {
        // Get food name - check enriched field first, then original
        const foodName = ing.food_bank_name || ing.food_item_name || ing.ingredient_name || ing.name || 'Unknown Item';
        const key = foodName.toLowerCase().trim();
        
        if (ingredientMap.has(key)) {
          const existing = ingredientMap.get(key);
          existing.grams += (ing.grams || 0) * portions;
        } else {
          ingredientMap.set(key, {
            name: foodName,
            grams: (ing.grams || 0) * portions,
            food_bank_id: ing.food_bank_id,
            category: ing.category || getCategoryFromName(foodName)
          });
        }
      });
    });

    // Group by category
    const grouped = {};
    
    ingredientMap.forEach((item, key) => {
      const category = item.category;

      if (!grouped[category]) {
        grouped[category] = [];
      }

      grouped[category].push({
        key,
        ...item
      });
    });

    setGroceryList(grouped);
  };

  const getCategoryFromName = (name) => {
    const nameLower = name.toLowerCase();
    
    // Meats & Proteins
    if (nameLower.includes('chicken') || nameLower.includes('beef') || nameLower.includes('pork') || 
        nameLower.includes('turkey') || nameLower.includes('salmon') || nameLower.includes('tuna') ||
        nameLower.includes('fish') || nameLower.includes('meat') || nameLower.includes('egg')) {
      return 'Meats & Proteins';
    }
    
    // Dairy
    if (nameLower.includes('milk') || nameLower.includes('cheese') || nameLower.includes('yogurt') ||
        nameLower.includes('butter') || nameLower.includes('cream')) {
      return 'Dairy';
    }
    
    // Vegetables
    if (nameLower.includes('spinach') || nameLower.includes('broccoli') || nameLower.includes('carrot') ||
        nameLower.includes('tomato') || nameLower.includes('onion') || nameLower.includes('pepper') ||
        nameLower.includes('cucumber') || nameLower.includes('lettuce') || nameLower.includes('potato') ||
        nameLower.includes('vegetable') || nameLower.includes('mushroom')) {
      return 'Vegetables';
    }
    
    // Fruits
    if (nameLower.includes('apple') || nameLower.includes('banana') || nameLower.includes('orange') ||
        nameLower.includes('berry') || nameLower.includes('blueberry') || nameLower.includes('strawberry') ||
        nameLower.includes('fruit') || nameLower.includes('mango')) {
      return 'Fruits';
    }
    
    // Grains
    if (nameLower.includes('rice') || nameLower.includes('pasta') || nameLower.includes('bread') ||
        nameLower.includes('oat') || nameLower.includes('quinoa') || nameLower.includes('wheat') ||
        nameLower.includes('flour') || nameLower.includes('cereal')) {
      return 'Grains';
    }
    
    return 'Other';
  };

  const formatAmount = (grams, itemName) => {
    // Convert certain items to whole units
    const nameLower = itemName.toLowerCase();
    
    // Eggs: ~50g per egg
    if (nameLower.includes('egg') && !nameLower.includes('eggplant')) {
      const count = Math.round(grams / 50);
      return `${count} egg${count !== 1 ? 's' : ''}`;
    }
    
    // Avocados: ~150g per avocado
    if (nameLower.includes('avocado')) {
      const count = Math.round(grams / 150);
      return `${count} avocado${count !== 1 ? 's' : ''}`;
    }
    
    // Bananas: ~120g per banana
    if (nameLower.includes('banana')) {
      const count = Math.round(grams / 120);
      return `${count} banana${count !== 1 ? 's' : ''}`;
    }
    
    // Apples: ~180g per apple
    if (nameLower.includes('apple')) {
      const count = Math.round(grams / 180);
      return `${count} apple${count !== 1 ? 's' : ''}`;
    }
    
    // Oranges: ~140g per orange
    if (nameLower.includes('orange')) {
      const count = Math.round(grams / 140);
      return `${count} orange${count !== 1 ? 's' : ''}`;
    }
    
    // Tomatoes: ~120g per tomato
    if (nameLower.includes('tomato')) {
      const count = Math.round(grams / 120);
      return `${count} tomato${count !== 1 ? 'es' : ''}`;
    }
    
    // Onions: ~150g per onion
    if (nameLower.includes('onion')) {
      const count = Math.round(grams / 150);
      return `${count} onion${count !== 1 ? 's' : ''}`;
    }
    
    // Potatoes: ~170g per potato
    if (nameLower.includes('potato')) {
      const count = Math.round(grams / 170);
      return `${count} potato${count !== 1 ? 'es' : ''}`;
    }
    
    // Default: show grams or kg
    if (grams >= 1000) {
      return `${(grams / 1000).toFixed(1)}kg`;
    }
    return `${Math.round(grams)}g`;
  };

  const categoryOrder = ['Meats & Proteins', 'Dairy', 'Vegetables', 'Fruits', 'Grains', 'Other'];
  const sortedCategories = Object.keys(groceryList).sort((a, b) => {
    const aIndex = categoryOrder.indexOf(a);
    const bIndex = categoryOrder.indexOf(b);
    return (aIndex === -1 ? 999 : aIndex) - (bIndex === -1 ? 999 : bIndex);
  });

  const categoryEmojis = {
    'Meats & Proteins': '🥩',
    'Vegetables': '🥬',
    'Fruits': '🍎',
    'Grains': '🌾',
    'Dairy': '🥛',
    'Other': '📦'
  };

  const totalItems = Object.values(groceryList).reduce((sum, items) => sum + items.length, 0);
  const checkedCount = checkedItems.size;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <ShoppingCart className="h-6 w-6 text-[#1a61e8]" />
            Grocery Shopping List
          </DialogTitle>
          <DialogDescription>
            {selectedRecipes.size} recipe{selectedRecipes.size !== 1 ? 's' : ''} selected • {totalItems} unique items
          </DialogDescription>
        </DialogHeader>

        {/* Portion Selector */}
        <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 mb-4">
          <Label className="text-sm font-semibold text-gray-700 mb-2 block">
            Meal Prep Portions
          </Label>
          <p className="text-xs text-gray-600 mb-3">
            Multiply ingredients for multiple days or extra servings
          </p>
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPortions(Math.max(1, portions - 1))}
              disabled={portions <= 1}
              className="h-10 w-10 p-0"
            >
              <Minus className="h-4 w-4" />
            </Button>
            <div className="flex-1 text-center">
              <div className="text-2xl font-bold text-[#1a61e8]">{portions}x</div>
              <div className="text-xs text-gray-500">
                {portions === 1 ? '1 portion' : `${portions} portions`}
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPortions(portions + 1)}
              className="h-10 w-10 p-0"
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <div className="space-y-4">
          {sortedCategories.length === 0 ? (
            <p className="text-center text-gray-500 py-8">No items to display</p>
          ) : (
            sortedCategories.map(category => {
              const items = groceryList[category];
              const categoryCheckedCount = items.filter(item => checkedItems.has(item.key)).length;

              return (
                <div key={category} className="bg-gray-50 rounded-xl p-4">
                  <h3 className="font-bold text-base mb-3 flex items-center gap-2 text-gray-900">
                    <span className="text-xl">{categoryEmojis[category] || '📦'}</span>
                    {category}
                    <span className="text-sm font-normal text-gray-500 ml-auto">
                      {items.length} item{items.length !== 1 ? 's' : ''}
                    </span>
                  </h3>
                  <div className="space-y-1">
                    {items.map(item => (
                      <div 
                        key={item.key} 
                        className="flex items-center gap-3 py-2.5 px-3 bg-white rounded-lg hover:shadow-sm transition-shadow"
                      >
                        <Checkbox
                          checked={checkedItems.has(item.key)}
                          onCheckedChange={(checked) => {
                            const newChecked = new Set(checkedItems);
                            if (checked) {
                              newChecked.add(item.key);
                            } else {
                              newChecked.delete(item.key);
                            }
                            setCheckedItems(newChecked);
                          }}
                          className="mt-0.5"
                        />
                        <div className="flex-1 flex items-center justify-between">
                          <span className={`font-medium text-gray-900 capitalize ${checkedItems.has(item.key) ? 'line-through text-gray-400' : ''}`}>
                            {item.name}
                          </span>
                          <span className={`text-sm font-semibold px-3 py-1 rounded-full ${
                            checkedItems.has(item.key) 
                              ? 'bg-gray-100 text-gray-400' 
                              : 'bg-blue-100 text-[#1a61e8]'
                          }`}>
                            {formatAmount(item.grams, item.name)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Summary Footer */}
        <div className="bg-gradient-to-r from-[#1a61e8] to-[#4a8ff5] text-white rounded-xl p-4 mt-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-blue-100">Shopping Progress</p>
              <p className="text-2xl font-bold">
                {checkedCount} / {totalItems}
              </p>
              <p className="text-xs text-blue-100 mt-1">
                {portions > 1 && `${portions}x portions • `}
                {checkedCount === totalItems && totalItems > 0 ? 'All done! 🎉' : `${totalItems - checkedCount} items left`}
              </p>
            </div>
            <div className="text-right">
              <Button 
                onClick={() => onOpenChange(false)}
                className="bg-white text-[#1a61e8] hover:bg-blue-50"
              >
                Done Shopping
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
